import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Single managed JSON-LD node (#zebo-jsonld).
 * The initial HTML for every public route already contains its route-owned graph
 * (build-time prerender, scripts/prerender_seo.py). On client-side navigation this
 * manager swaps that one script's content to the target route's graph — fetched
 * from /seo-graphs/<route>.json — so the rendered DOM always matches the URL and
 * scripts never accumulate. Unknown routes (404) get NO promotional schema.
 */
const cache = new Map();

export default function SchemaManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.replace(/\/+$/, "") || "/";
    const url = `/seo-graphs${path === "/" ? "/home" : path}.json`;

    const apply = (graph) => {
      document.head
        .querySelectorAll('script[type="application/ld+json"]:not(#zebo-jsonld)')
        .forEach((n) => n.remove());
      let node = document.getElementById("zebo-jsonld");
      if (!graph) {
        if (node) node.remove();
        return;
      }
      if (!node) {
        node = document.createElement("script");
        node.type = "application/ld+json";
        node.id = "zebo-jsonld";
        document.head.appendChild(node);
      }
      node.textContent = JSON.stringify(graph);
    };

    if (cache.has(url)) {
      apply(cache.get(url));
      return;
    }
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((graph) => {
        cache.set(url, graph);
        apply(graph);
      })
      .catch(() => apply(null));
  }, [pathname]);

  return null;
}
